import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { esitoUpload } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { X, Search, Plus, Loader2, UserPlus, ArrowRight, ArrowLeft, Check,
  FileText, Upload, FolderOpen, AlertTriangle } from "lucide-react";
import UploadDialog from "@/components/UploadDialog";
import { ImportDocsButtons } from "@/components/ImportDocsButtons";
import { eur } from "@/lib/format";
import AnagraficaFieldsGroups, { requisitiCliente, campiResidenzaNonPertinenti } from "@/components/AnagraficaFieldsGroups";
import ReferenteSelect from "@/components/ReferenteSelect";

const STEPS = [{ n: 1, l: "Cliente" }, { n: 2, l: "Tipo pratica" }, { n: 3, l: "Documenti & dettagli" }];
const EMPTY_CLI = { nome: "", cognome: "", email: "", telefono: "", note: "", codice_fiscale: "", data_nascita: "",
  luogo_nascita: "", nazionalita: "", indirizzo: "", residenza_citta: "", numero_passaporto: "", data_rilascio: "",
  luogo_rilascio: "", data_scadenza: "", numero_documento: "", scadenza_documento: "", rilascio_documento: "",
  numero_permesso_soggiorno: "", scadenza_permesso: "", residenza_tipo: "", residenza_provincia: "",
  residenza_cap: "", residenza_stato: "", luogo_rilascio_permesso: "", data_emissione_permesso: "",
  luogo_rilascio_documento: "", referente_id: null };
const uid = () => Math.random().toString(36).slice(2);
const PERMESSO_KEYS = ["numero_permesso_soggiorno", "scadenza_permesso", "luogo_rilascio_permesso", "data_emissione_permesso"];
const stripExt = (n) => (n || "").replace(/\.[^.]+$/, "");

export default function NuovaPraticaDialog({ onClose, initialClienteId = "" }) {
  const [origine, setOrigine] = useState("diretta");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: clienti } = useQuery({ queryKey: ["clienti"], queryFn: async () => (await api.get("/clienti")).data });
  const { data: tipi } = useQuery({ queryKey: ["tipi-pratica"], queryFn: async () => (await api.get("/tipi-pratica")).data });
  const { data: anaFields } = useQuery({ queryKey: ["anagrafica-fields"], queryFn: async () => (await api.get("/anagrafica-fields")).data });
  const { data: gruppi } = useQuery({ queryKey: ["tipi-gruppi"], queryFn: async () => (await api.get("/tipi-pratica/gruppi")).data });

  const [step, setStep] = useState(1);
  const [gruppo, setGruppo] = useState("");
  const [tuttiTipi, setTuttiTipi] = useState(false);
  const [docAtteso, setDocAtteso] = useState("");
  const [busy, setBusy] = useState(false);
  const [clienteId, setClienteId] = useState(initialClienteId);
  const [cliQuery, setCliQuery] = useState("");
  const [cliOpen, setCliOpen] = useState(false);
  const [newMode, setNewMode] = useState(false);
  const [cliForm, setCliForm] = useState(EMPTY_CLI);
  const [cliExtra, setCliExtra] = useState({});
  const [editAna, setEditAna] = useState(false);
  const [forzaDati, setForzaDati] = useState(false);
  const [notaForza, setNotaForza] = useState("");
  const [tipoId, setTipoId] = useState("");
  const [valore, setValore] = useState("");
  const [haSponsor, setHaSponsor] = useState(false);
  const [sponsor, setSponsor] = useState({ nome: "", contatti: "", rapporto: "" });
  const [acc, setAcc] = useState({ importo: "", data: "", nota: "" });
  const [acconti, setAcconti] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [newFiles, setNewFiles] = useState([]);

  const selCli = (clienti || []).find((c) => c.id === clienteId);
  const filtered = (clienti || []).filter((c) =>
    `${c.nome} ${c.cognome}`.toLowerCase().includes(cliQuery.trim().toLowerCase())).slice(0, 60);
  const canStep1 = !!clienteId || (newMode && cliForm.nome.trim());
  const selTipo = (tipi || []).find((t) => t.id === tipoId);
  const contaGruppo = (k) => (tipi || []).filter((t) => (t.gruppo || "") === k).length;
  const tipiVisibili = tuttiTipi ? (tipi || []) : (tipi || []).filter((t) => (t.gruppo || "") === gruppo);
  const docRichiesti = selTipo?.documenti_richiesti || [];
  const clienteNome = newMode ? `${cliForm.nome} ${cliForm.cognome}`.trim() : (selCli ? `${selCli.nome} ${selCli.cognome}` : "");

  // Carica l'anagrafica del cliente esistente selezionato (per completarla/modificarla)
  useEffect(() => {
    if (selCli) {
      const loaded = { ...EMPTY_CLI };
      Object.keys(EMPTY_CLI).forEach((k) => { loaded[k] = selCli[k] ?? ""; });
      setCliForm(loaded);
      setCliExtra(selCli.campi_extra || {});
      setEditAna(false);
    }
  }, [clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cittadinanza italiana -> i campi del permesso di soggiorno non sono obbligatori
  const cittadinanza = String(cliForm.nazionalita || "").trim().toLowerCase();
  const isItaliano = ["italiana", "italiano", "italia", "it", "ita"].includes(cittadinanza) || cittadinanza.includes("italian");
  const nonPertinenti = campiResidenzaNonPertinenti(cliForm.residenza_tipo);
  const requiredKeys = (selTipo?.campi_obbligatori || [])
    .filter((k) => !(isItaliano && PERMESSO_KEYS.includes(k)) && !nonPertinenti.includes(k));
  const labelMap = {};
  (anaFields?.fields || []).forEach((f) => { labelMap[f.key] = f.label; });
  Object.assign(labelMap, { nome: "Nome", cognome: "Cognome", email: "Email", telefono: "Telefono" });
  const anaGet = (f) => (f.builtin ? (cliForm[f.key] ?? "") : (cliExtra[f.key] ?? ""));
  const anaSet = (f, v) => (f.builtin ? setCliForm((p) => ({ ...p, [f.key]: v })) : setCliExtra((p) => ({ ...p, [f.key]: v })));
  const anaValueByKey = (k) => (cliForm[k] ?? cliExtra[k] ?? "");
  const requiredMissing = requiredKeys.filter((k) => !String(anaValueByKey(k) || "").trim());

  const { data: docStato, refetch: refetchDocStato } = useQuery({
    queryKey: ["np-doc-stato", selCli?.id, tipoId],
    queryFn: async () => (await api.get(`/clienti/${selCli.id}/documenti-stato`, { params: { tipo_id: tipoId } })).data,
    enabled: !!selCli?.id && !newMode && !!tipoId,
  });
  const causali = docStato?.causali || [];
  const [docMancanti, setDocMancanti] = useState({});
  const segnaMancante = (d, on) => setDocMancanti((prev) => {
    const copia = { ...prev };
    if (on) copia[d.key] = { key: d.key, nome: d.nome, motivo: causali[0] || "In attesa dal cliente", note: "" };
    else delete copia[d.key];
    return copia;
  });

  const { data: driveFiles, refetch: refetchDrive } = useQuery({
    queryKey: ["np-drive", selCli?.id],
    queryFn: async () => (await api.get(`/clienti/${selCli.id}/drive-files`)).data,
    enabled: !!selCli?.id && !newMode,
  });

  const addAcconto = () => {
    if (!(Number(acc.importo) > 0)) return toast.error("Importo acconto non valido");
    setAcconti([...acconti, { uid: Math.random().toString(36).slice(2, 9), importo: Number(acc.importo), data: acc.data, nota: acc.nota }]);
    setAcc({ importo: "", data: "", nota: "" });
  };
  const removeAcconto = (id) => setAcconti(acconti.filter((a) => a.uid !== id));

  const { can } = useAuth();

  // La pratica viene creata SOLO qui: "Crea pratica" o "Salva bozza". Chiudendo prima, nulla viene salvato.
  const submit = async (asBozza) => {
    if (!tipoId) return toast.error("Scegli un tipo pratica");
    if (newMode) {
      const mancantiBase = requisitiCliente((k) => cliForm[k]).filter((k) => !String(cliForm[k] || "").trim());
      if (mancantiBase.length) return toast.error(`Dati obbligatori del nuovo cliente mancanti: ${mancantiBase.map((k) => labelMap[k] || k).join(", ")}`);
    }
    if (requiredMissing.length > 0 && !(forzaDati && can("gestione_impostazioni"))) {
      toast.error(`Completa i campi obbligatori per ${selTipo?.nome}: ${requiredMissing.map((k) => labelMap[k] || k).join(", ")}`);
      setStep(1);
      setEditAna(true);
      return;
    }
    setBusy(true);
    try {
      let cli = selCli;
      if (newMode) {
        const { data } = await api.post("/clienti", { ...cliForm, campi_extra: cliExtra });
        cli = data;
        if (newFiles.length > 0) {
          const fd = new FormData();
          newFiles.forEach((it) => { fd.append("files", it.file); fd.append("names", it.name || stripExt(it.file.name)); });
          try {
            const { data: up } = await api.post(`/clienti/${cli.id}/upload`, fd);
            toast.success(esitoUpload(up));
          } catch (e) {
            toast.warning(e?.response?.data?.detail || "Cliente creato, ma upload documenti non riuscito");
          }
        }
      } else if (cli) {
        // Salva eventuali modifiche/completamenti all'anagrafica del cliente esistente
        await api.patch(`/clienti/${cli.id}/anagrafica`, { ...cliForm, campi_extra: cliExtra });
      }
      const { data: p } = await api.post("/pratiche", {
        forzato: requiredMissing.length > 0 && forzaDati,
        forzatura_nota: notaForza,
        cliente_id: cli.id, tipo_pratica_id: tipoId, origine,
        documenti_mancanti: Object.values(docMancanti),
        ha_sponsor: haSponsor,
        dati_sponsor: haSponsor ? {
          "Nome e cognome sponsor": sponsor.nome,
          "Contatti sponsor": sponsor.contatti,
          "Rapporto con il richiedente": sponsor.rapporto,
        } : {},
      });
      const patch = {};
      if (Number(valore) > 0) patch.importo_totale = Number(valore);
      if (asBozza) patch.attiva = false;
      if (Object.keys(patch).length) await api.put(`/pratiche/${p.id}/dettagli`, patch);
      for (const a of acconti) {
        await api.post(`/pratiche/${p.id}/pagamenti`, { importo: a.importo, data: a.data || undefined, nota: a.nota });
      }
      qc.invalidateQueries({ queryKey: ["pratiche"] });
      qc.invalidateQueries({ queryKey: ["clienti"] });
      qc.invalidateQueries({ queryKey: ["contabilita"] });
      toast.success(asBozza ? "Bozza salvata (pratica in pausa)" : "Pratica creata");
      onClose();
      if (!asBozza) navigate(`/pratiche/${p.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione pratica"); }
    finally { setBusy(false); }
  };

  const files = driveFiles?.files || [];

  const renderAnaForm = () => (
    <div className="space-y-4">
      <AnagraficaFieldsGroups getValue={anaGet} setValue={anaSet} requiredKeys={requiredKeys} tipiPratica={tipoId ? [tipoId] : []} baseRequired={!selCli} />
      <ReferenteSelect value={cliForm.referente_id} onChange={(id) => setCliForm((p) => ({ ...p, referente_id: id }))} testid="np-referente" />
    </div>
  );

  const renderNewUpload = () => (
    <div data-testid="np-new-upload" className="rounded-lg border border-[#1A2B4A]/15 bg-white p-4">
      <label className="block text-sm font-semibold text-[#1A2B4A] mb-1 flex items-center gap-2"><FileText className="w-4 h-4 text-[#C9A84C]" /> Documenti (opzionale · autocompilazione AI)</label>
      <p className="text-xs text-[#4a5568] mb-2">Importa carta d'identità o passaporto: i dati anagrafici si compilano automaticamente e i file vengono caricati sulla cartella Drive del cliente al salvataggio. Controlla e correggi i dati estratti.</p>
      <ImportDocsButtons currentValues={{ ...cliForm, ...cliExtra }} onExtract={(f, enriched) => {
        setCliForm((prev) => ({ ...prev, ...f }));
        const cognome = (f.cognome || cliForm.cognome || "").trim();
        const nome = (f.nome || cliForm.nome || "").trim();
        const cli = [cognome, nome].filter(Boolean).join(" ");
        if (enriched?.length) setNewFiles((prev) => [...prev, ...enriched.map(({ file, tipo }) => {
          const base = (tipo || stripExt(file.name) || "Documento").trim();
          return { uid: uid(), file, name: cli ? `${base} (${cli})` : base };
        })]);
      }} />
      {newFiles.length > 0 && (
        <div className="mt-3 space-y-2">
          {newFiles.map((it, i) => (
            <div key={it.uid} className="border border-[#1A2B4A]/10 rounded-lg p-2.5">
              <div className="flex items-center gap-2 text-[11px] text-[#4a5568] mb-1.5">
                <FileText className="w-3.5 h-3.5" /> <span className="truncate">{it.file.name}</span>
                <button data-testid={`np-file-remove-${i}`} className="ml-auto" onClick={() => setNewFiles(newFiles.filter((_, j) => j !== i))}><X className="w-3.5 h-3.5 text-[#9b2c2c]" /></button>
              </div>
              <input data-testid={`np-doc-name-${i}`} value={it.name}
                onChange={(e) => setNewFiles(newFiles.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                placeholder="es. Passaporto"
                className="w-full px-3 py-1.5 text-sm bg-white border border-[#1A2B4A]/20 rounded-lg focus:outline-none focus:border-[#C9A84C]" />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-[#16233c]/40 backdrop-blur-sm backdrop-blur-sm flex items-center justify-center p-4" data-testid="nuova-pratica-dialog">
      <div className="bg-white rounded-[1.5rem] w-full max-w-2xl max-h-[92vh] overflow-y-auto border-t-2 border-[#C9A84C] shadow-lg shadow-[0_20px_50px_rgba(0,0,0,0.15)]">
        <div className="sticky top-0 bg-white z-10 px-5 pt-5 pb-3 border-b border-[#1A2B4A]/8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-2xl font-semibold tracking-tight text-[#1A2B4A]" data-testid="np-title">Nuova pratica{selTipo?.nome ? ` di ${selTipo.nome}` : ""}</h3>
            <button data-testid="np-close" onClick={onClose} className="text-[#4a5568] hover:text-[#9b2c2c]"><X className="w-6 h-6" /></button>
          </div>
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => (
              <div key={s.n} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${
                  step > s.n ? "bg-[#C9A84C] border-[#C9A84C] text-white" :
                  step === s.n ? "bg-white border-[#C9A84C] text-[#C9A84C]" : "bg-white border-[#1A2B4A]/20 text-[#1A2B4A]/40"}`}>
                  {step > s.n ? <Check className="w-3.5 h-3.5" /> : s.n}
                </div>
                <span className={`text-xs ${step === s.n ? "text-[#1A2B4A] font-semibold" : "text-[#4a5568]"}`}>{s.l}</span>
                {i < STEPS.length - 1 && <div className="w-6 h-0.5 bg-[#1A2B4A]/15" />}
              </div>
            ))}
          </div>
        </div>

        <div className="p-4">
          {/* STEP 1: CLIENTE */}
          {step === 1 && (
            <div>
              {!newMode ? (
                <>
                  <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Seleziona cliente</label>
                  <div className="relative mt-1 mb-4">
                    <div className="flex items-center border border-[#1A2B4A]/15 rounded-md px-3 focus-within:ring-2 focus-within:ring-[#C9A84C]/40">
                      <Search className="w-4 h-4 text-[#4a5568] shrink-0" />
                      <input data-testid="np-cliente-search"
                        value={selCli && !cliOpen ? `${selCli.nome} ${selCli.cognome}` : cliQuery}
                        onChange={(e) => { setCliQuery(e.target.value); setCliOpen(true); if (clienteId) setClienteId(""); }}
                        onFocus={() => setCliOpen(true)} placeholder="Cerca cliente per nome…"
                        className="w-full px-2 py-2.5 bg-transparent focus:outline-none text-sm" />
                    </div>
                    {cliOpen && <div className="fixed inset-0 z-10" onClick={() => setCliOpen(false)} />}
                    {cliOpen && (
                      <div className="absolute z-20 mt-1 w-full max-h-52 overflow-y-auto bg-white border border-[#1A2B4A]/12 rounded-md shadow-lg">
                        {filtered.length === 0 && <div className="px-3 py-2 text-sm text-[#4a5568]">Nessun cliente trovato.</div>}
                        {filtered.map((c) => (
                          <button key={c.id} data-testid={`np-cliente-option-${c.id}`}
                            onClick={() => { setClienteId(c.id); setCliOpen(false); setCliQuery(""); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-[#f9f8f5] transition-colors">{c.nome} {c.cognome}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button data-testid="np-new-cliente-toggle" onClick={() => { setNewMode(true); setClienteId(""); setCliForm(EMPTY_CLI); setCliExtra({}); }}
                    className="inline-flex items-center gap-2 text-sm text-[#1A2B4A] hover:text-[#C9A84C] transition-colors">
                    <UserPlus className="w-4 h-4" /> Il cliente non esiste? Crea nuovo cliente
                  </button>
                  {selCli && (
                    <div className="mt-5">
                      <button data-testid="np-edit-ana-toggle" onClick={() => setEditAna((v) => !v)}
                        className="inline-flex items-center gap-2 text-sm border border-[#1A2B4A]/20 px-3 py-1.5 rounded-sm hover:border-[#C9A84C]">
                        {editAna ? "Nascondi anagrafica" : "Completa / modifica anagrafica"}
                      </button>
                      {requiredMissing.length > 0 && (
                        <span className="ml-2 text-[11px] text-[#9b2c2c]">Mancano campi obbligatori per {selTipo?.nome}</span>
                      )}
                      {requiredMissing.length > 0 && can("gestione_impostazioni") && (
                        <div data-testid="np-forzatura" className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-[#8a93a6]">
                          <label className="inline-flex items-center gap-1.5 cursor-pointer hover:text-[#1A2B4A]">
                            <input data-testid="np-forzatura-spunta" type="checkbox" checked={forzaDati}
                              onChange={(e) => setForzaDati(e.target.checked)} className="w-3 h-3 accent-[#C9A84C]" />
                            Procedi comunque
                          </label>
                          {forzaDati && (
                            <input data-testid="np-forzatura-nota" value={notaForza} onChange={(e) => setNotaForza(e.target.value)}
                              placeholder="Nota, se vuoi spiegare il perché (facoltativa)"
                              className="flex-1 min-w-[220px] px-2 py-1 rounded-md border border-[#C9A84C]/50 bg-white text-[11px] text-[#1A2B4A] focus:border-[#C9A84C] outline-none" />
                          )}
                          {forzaDati && <span className="text-[#8C2A2A]">I dati restano segnati come mancanti e resta scritto nello storico.</span>}
                        </div>
                      )}
                      {editAna && <div className="mt-4">{renderAnaForm()}</div>}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-[#1A2B4A]">Nuovo cliente</span>
                    <button data-testid="np-cancel-new-cliente" onClick={() => { setNewMode(false); setCliForm(EMPTY_CLI); setCliExtra({}); }} className="text-xs text-[#4a5568] hover:text-[#1A2B4A]">← usa cliente esistente</button>
                  </div>
                  {requiredKeys.length > 0 && (
                    <p className="text-[11px] text-[#4a5568] mb-3">I campi con <span className="text-[#9b2c2c]">*</span> sono obbligatori per la pratica <b>{selTipo?.nome}</b>.</p>
                  )}
                  {renderAnaForm()}
                  {renderNewUpload()}
                  <p className="text-[11px] text-[#4a5568] mt-3">Verrà creata automaticamente la cartella cliente su Google Drive.</p>
                </>
              )}
            </div>
          )}

          {/* STEP 2: TIPO PRATICA */}
          {step === 2 && (
            <div>
              {!gruppo && !tuttiTipi ? (
                <div data-testid="np-gruppi">
                  <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-3 block">Di cosa ha bisogno il cliente?</label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {(gruppi || []).filter((g) => contaGruppo(g.key) > 0).map((g) => (
                      <button key={g.key} data-testid={`np-gruppo-${g.key}`} onClick={() => setGruppo(g.key)}
                        className="text-left p-4 rounded-md border border-[#1A2B4A]/12 hover:border-[#C9A84C] transition-all">
                        <div className="font-semibold text-[#1A2B4A] text-sm">{g.label}</div>
                        <div className="text-[11px] text-[#4a5568] mt-1">{contaGruppo(g.key)} {contaGruppo(g.key) === 1 ? "tipo" : "tipi"}</div>
                      </button>
                    ))}
                  </div>
                  <button data-testid="np-vedi-tutti" onClick={() => setTuttiTipi(true)}
                    className="mt-4 text-xs underline text-[#1A2B4A] hover:text-[#C9A84C]">Vedi tutti i tipi →</button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-3 gap-2">
                    <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">
                      {tuttiTipi ? "Tutti i tipi di pratica" : (gruppi || []).find((g) => g.key === gruppo)?.label}
                    </label>
                    <button data-testid="np-cambia-gruppo" onClick={() => { setGruppo(""); setTuttiTipi(false); setTipoId(""); }}
                      className="text-xs underline text-[#1A2B4A] hover:text-[#C9A84C]">← Cambia esigenza</button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {tipiVisibili.map((t) => (
                      <button key={t.id} data-testid={`np-tipo-${t.id}`} onClick={() => setTipoId(t.id)}
                        className={`text-left p-4 rounded-md border transition-all ${tipoId === t.id ? "border-[#C9A84C] bg-[#C9A84C]/5 ring-2 ring-[#C9A84C]/30" : "border-[#1A2B4A]/12 hover:border-[#C9A84C]"}`}>
                        <div className="font-semibold text-[#1A2B4A] text-sm">{t.nome}</div>
                        {t.descrizione ? (
                          <div className="text-[11px] text-[#4a5568] mt-1">{t.descrizione}</div>
                        ) : (
                          <div className="text-[11px] text-[#8a93a6] mt-1 italic">descrizione da compilare in Tipi Pratica</div>
                        )}
                      </button>
                    ))}
                    {tipiVisibili.length === 0 && <p className="text-sm text-[#4a5568]">Nessun tipo in questa voce. Assegna il gruppo ai tipi in "Tipi Pratica".</p>}
                  </div>
                </div>
              )}

              <div className="mt-5">
                <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-1.5 block">Come nasce questa pratica</label>
                <select data-testid="np-origine" value={origine} onChange={(e) => setOrigine(e.target.value)}
                  className="w-full sm:w-72 px-3 py-2 bg-white border border-[#1A2B4A]/15 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/40">
                  <option value="diretta">Diretta</option>
                  <option value="spazio_ascolto_hirundo">Spazio ascolto Hirundo</option>
                  <option value="segnalazione">Segnalazione</option>
                </select>
              </div>
            </div>
          )}

          {/* STEP 3: DOCUMENTI & DETTAGLI */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-[#f9f8f5] rounded-md p-4 flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#C9A84C]" />
                <span className="text-sm text-[#1A2B4A]">Riepilogo: pratica <b>{selTipo?.nome || "—"}</b> per <b>{clienteNome || "—"}</b>. La pratica sarà creata solo al termine.</span>
              </div>

              {!newMode && selCli && (
                <ReferenteSelect value={cliForm.referente_id || ""} onChange={(rid) => setCliForm((p) => ({ ...p, referente_id: rid }))} testid="np-rif-existing" />
              )}

              {requiredKeys.length > 0 && (
                requiredMissing.length === 0 ? (
                  <div data-testid="np-req-ok" className="text-xs text-[#276749] bg-[#f0f6f2] border border-[#276749]/20 rounded-md p-3 flex items-center gap-2">
                    <Check className="w-4 h-4 shrink-0" /> Campi anagrafici obbligatori completi.
                  </div>
                ) : (
                  <div data-testid="np-req-missing" className="text-xs bg-[#fdf6f6] border border-[#9b2c2c]/25 rounded-md p-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#9b2c2c] shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#9b2c2c] font-medium">Mancano campi obbligatori per {selTipo?.nome}:</p>
                      <p className="text-[#4a5568] mt-0.5">{requiredMissing.map((k) => labelMap[k] || k).join(", ")}</p>
                      <button data-testid="np-goto-ana" onClick={() => { setStep(1); setEditAna(true); }} className="mt-1 underline text-[#1A2B4A] hover:text-[#C9A84C]">Vai a compilarli →</button>
                    </div>
                  </div>
                )
              )}

              {/* Campi personalizzati collegati a questo tipo di pratica */}
              {tipoId && (
                <div data-testid="np-campi-tipo">
                  <AnagraficaFieldsGroups onlyCustom tipiPratica={[tipoId]}
                    getValue={anaGet} setValue={anaSet} requiredKeys={requiredKeys} />
                </div>
              )}

              {/* Documenti richiesti dal tipo scelto, uno per uno */}
              {tipoId && (
                <div data-testid="np-doc-richiesti">
                  <h4 className="text-sm font-semibold text-[#1A2B4A] flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-[#C9A84C]" /> Documenti richiesti per «{selTipo?.nome}»
                  </h4>
                  {docRichiesti.length === 0 ? (
                    <p className="text-xs text-[#4a5568] bg-[#fdfaf1] border border-[#C9A84C]/30 rounded-md p-3">
                      Nessun documento configurato per questo tipo: si impostano in <b>Impostazioni → Tipi Pratica</b>, sezione «Documenti richiesti».
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {docRichiesti.map((d0, i) => {
                        const dk = d0.key || (d0.nome || "").toLowerCase().replace(/[^a-z0-9]+/g, "_");
                        const st = (docStato?.documenti || []).find((x) => x.key === dk) || {};
                        const d = { ...d0, key: dk };
                        const inArchivio = !!st.presente;
                        const inCoda = newFiles.some((f) => (f.name || "").toLowerCase().includes((d.nome || "").toLowerCase()));
                        const ok = inArchivio || inCoda;
                        const segnato = !!docMancanti[dk];
                        return (
                          <li key={dk || i} data-testid={`np-doc-${i}`}
                            className={`flex flex-wrap items-center gap-2 border rounded-md px-3 py-2 ${ok ? "border-[#276749]/25 bg-[#f0f6f2]" : segnato ? "border-[#C9A84C]/50 bg-[#fdfaf1]" : d.obbligatorio ? "border-[#9b2c2c]/25 bg-[#fdf6f6]" : "border-[#1A2B4A]/12"}`}>
                            {ok ? <Check className="w-4 h-4 text-[#276749] shrink-0" /> : <AlertTriangle className={`w-4 h-4 shrink-0 ${segnato ? "text-[#C9A84C]" : d.obbligatorio ? "text-[#9b2c2c]" : "text-[#8a93a6]"}`} />}
                            <span className="text-[12px] text-[#1A2B4A] min-w-0 flex-1">
                              {d.nome}{d.obbligatorio ? " *" : ""}
                              <span data-testid={`np-doc-stato-${i}`} className="text-[11px] text-[#4a5568]">
                                {" · "}
                                {inArchivio ? `già presente · ${st.file}${st.fonte === "drive" ? " (Drive)" : ""}`
                                  : inCoda ? "pronto da caricare"
                                  : segnato ? `segnato mancante · ${docMancanti[dk].motivo}` : "mancante"}
                              </span>
                            </span>
                            {!ok && !newMode && selCli && (
                              <label data-testid={`np-doc-mancante-wrap-${i}`} className="inline-flex items-center gap-1.5 text-[11px] text-[#7a5c12] cursor-pointer">
                                <input type="checkbox" data-testid={`np-doc-mancante-${i}`} checked={segnato}
                                  onChange={(e) => segnaMancante(d, e.target.checked)} className="accent-[#C9A84C]" />
                                mancante
                              </label>
                            )}
                            {segnato && (
                              <div className="w-full flex flex-wrap items-center gap-2 pl-6">
                                <select data-testid={`np-doc-causale-${i}`} value={docMancanti[dk].motivo}
                                  onChange={(e) => setDocMancanti((prev) => ({ ...prev, [dk]: { ...prev[dk], motivo: e.target.value } }))}
                                  className="text-[11px] px-2 py-1 border border-[#1A2B4A]/20 rounded-md bg-white">
                                  {causali.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <input data-testid={`np-doc-note-${i}`} value={docMancanti[dk].note} placeholder="note (facoltative)"
                                  onChange={(e) => setDocMancanti((prev) => ({ ...prev, [dk]: { ...prev[dk], note: e.target.value } }))}
                                  className="flex-1 min-w-[140px] text-[11px] px-2 py-1 border border-[#1A2B4A]/20 rounded-md" />
                              </div>
                            )}
                            {!ok && !segnato && (!newMode && selCli ? (
                              <button data-testid={`np-doc-carica-${i}`} onClick={() => { setDocAtteso(d.nome); setUploadOpen(true); }}
                                className="inline-flex items-center gap-1.5 text-xs bg-[#1A2B4A] text-white px-3 py-1.5 rounded-md hover:bg-[#22345c]">
                                <Upload className="w-3.5 h-3.5" /> Carica
                              </button>
                            ) : (
                              <label className="inline-flex items-center gap-1.5 text-xs bg-[#1A2B4A] text-white px-3 py-1.5 rounded-md hover:bg-[#22345c] cursor-pointer">
                                <Upload className="w-3.5 h-3.5" /> Carica
                                <input data-testid={`np-doc-file-${i}`} type="file" className="hidden"
                                  onChange={(e) => { const f = e.target.files?.[0]; if (f) setNewFiles((prev) => [...prev, { uid: uid(), file: f, name: d.nome }]); e.target.value = ""; }} />
                              </label>
                            ))}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}

              {/* Documenti (solo per cliente esistente) */}
              {!newMode && selCli ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-[#1A2B4A] flex items-center gap-2"><FileText className="w-4 h-4 text-[#C9A84C]" /> Documenti del cliente</h4>
                    <button data-testid="np-upload-btn" onClick={() => setUploadOpen(true)}
                      className="inline-flex items-center gap-1.5 text-xs bg-[#1A2B4A] text-white px-3 py-1.5 rounded-md hover:bg-[#22345c] transition-all">
                      <Upload className="w-3.5 h-3.5" /> Carica (archivio/foto)
                    </button>
                  </div>
                  <div className="border border-[#1A2B4A]/10 rounded-md p-3 max-h-40 overflow-y-auto" data-testid="np-docs-preview">
                    {files.length === 0 ? (
                      <p className="text-xs text-[#4a5568] py-2 text-center">Nessun documento disponibile per questo cliente.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {files.map((f) => (
                          <li key={f.id} className="flex items-center gap-2 text-sm text-[#1A2B4A]">
                            <FolderOpen className="w-3.5 h-3.5 text-[#C9A84C] shrink-0" />
                            <span className="truncate">{f.name}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-[#4a5568] bg-[#f9f8f5] rounded-md p-3">{newFiles.length > 0 ? `${newFiles.length} documento/i pronti da caricare su Drive alla creazione.` : "I documenti sono stati aggiunti nello step \"Cliente\"; verranno caricati su Drive alla creazione."}</p>
              )}

              {/* Valore */}
              <div>
                <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Valore pratica (€)</label>
                <input data-testid="np-valore" type="number" value={valore} onChange={(e) => setValore(e.target.value)} placeholder={selTipo?.importo_default ? `predefinito ${selTipo.importo_default}` : "importo da definire"}
                  className="w-full mt-1 px-3 py-2 text-sm border border-[#1A2B4A]/15 rounded-md focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/40" />
              </div>

              {/* Sponsor: facoltativo, si chiede qui e non compare più nelle schede senza sponsor */}
              <div data-testid="np-sponsor-box" className="rounded-lg border border-[#1A2B4A]/12 bg-[#fbfaf7] p-3">
                <p className="text-[13px] font-medium text-[#1A2B4A]">C'è uno sponsor o un segnalatore?</p>
                <div className="flex gap-2 mt-2">
                  {[["no", false], ["sì", true]].map(([l, v]) => (
                    <button key={l} data-testid={`np-sponsor-${v ? "si" : "no"}`} onClick={() => setHaSponsor(v)}
                      className={`px-4 min-h-[38px] rounded-lg border text-[13px] font-semibold transition-colors
                        ${haSponsor === v ? "border-[#C9A84C] bg-white text-[#1A2B4A]" : "border-[#1A2B4A]/12 text-[#4a5568] hover:border-[#C9A84C]/60"}`}>
                      {l}
                    </button>
                  ))}
                </div>
                {haSponsor && (
                  <div className="grid sm:grid-cols-3 gap-2 mt-3">
                    <input data-testid="np-sponsor-nome" placeholder="Nome e cognome" value={sponsor.nome}
                      onChange={(e) => setSponsor({ ...sponsor, nome: e.target.value })}
                      className="px-3 py-2 text-sm border border-[#1A2B4A]/15 rounded-md focus:outline-none focus:border-[#C9A84C]" />
                    <input data-testid="np-sponsor-contatti" placeholder="Telefono o email" value={sponsor.contatti}
                      onChange={(e) => setSponsor({ ...sponsor, contatti: e.target.value })}
                      className="px-3 py-2 text-sm border border-[#1A2B4A]/15 rounded-md focus:outline-none focus:border-[#C9A84C]" />
                    <input data-testid="np-sponsor-rapporto" placeholder="Rapporto con il cliente" value={sponsor.rapporto}
                      onChange={(e) => setSponsor({ ...sponsor, rapporto: e.target.value })}
                      className="px-3 py-2 text-sm border border-[#1A2B4A]/15 rounded-md focus:outline-none focus:border-[#C9A84C]" />
                  </div>
                )}
              </div>

              {/* Acconti (raccolti localmente, registrati alla creazione) */}
              <div>
                <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Acconti / Pagamenti</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  <input data-testid="np-acconto-importo" type="number" placeholder="Importo €" value={acc.importo} onChange={(e) => setAcc({ ...acc, importo: e.target.value })}
                    className="w-28 px-2 py-2 text-sm border border-[#1A2B4A]/15 rounded-md focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/40" />
                  <input data-testid="np-acconto-data" type="date" value={acc.data} onChange={(e) => setAcc({ ...acc, data: e.target.value })}
                    className="px-2 py-2 text-sm border border-[#1A2B4A]/15 rounded-md focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/40" />
                  <input data-testid="np-acconto-nota" placeholder="Nota" value={acc.nota} onChange={(e) => setAcc({ ...acc, nota: e.target.value })}
                    className="flex-1 min-w-[80px] px-2 py-2 text-sm border border-[#1A2B4A]/15 rounded-md focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/40" />
                  <button data-testid="np-acconto-add" onClick={addAcconto} title="Aggiungi acconto"
                    className="px-3 bg-[#1A2B4A] text-white rounded-md hover:bg-[#22345c] transition-all"><Plus className="w-4 h-4" /></button>
                </div>
                {acconti.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {acconti.map((a, i) => (
                      <li key={a.uid} className="flex items-center justify-between text-sm border-b border-[#1A2B4A]/8 pb-1.5">
                        <span className="text-[#276749] font-medium">{eur(a.importo)}{a.data ? ` · ${a.data}` : ""}{a.nota ? ` · ${a.nota}` : ""}</span>
                        <button data-testid={`np-acconto-remove-${i}`} onClick={() => removeAcconto(a.uid)} className="text-[#9b2c2c] hover:text-[#7a1f1f]"><X className="w-3.5 h-3.5" /></button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[11px] text-[#4a5568] mt-1">Le fatture potrai generarle dal dettaglio pratica o dalla Contabilità.</p>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER NAV */}
        <div className="sticky bottom-0 bg-white border-t border-[#1A2B4A]/8 px-5 py-2.5 flex items-center justify-between">
          <button onClick={step === 1 ? onClose : () => setStep(step - 1)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-[#1A2B4A] rounded-md hover:bg-[#f9f8f5] transition-colors">
            <ArrowLeft className="w-4 h-4" /> {step === 1 ? "Annulla" : "Indietro"}
          </button>
          {step === 1 && (
            <button data-testid="np-next-1" onClick={() => setStep(2)} disabled={!canStep1}
              className="inline-flex items-center gap-1.5 px-5 py-2 text-sm bg-[#1A2B4A] text-white rounded-md font-semibold hover:bg-[#22345c] transition-all disabled:opacity-40">
              Avanti <ArrowRight className="w-4 h-4" />
            </button>
          )}
          {step === 2 && (
            <button data-testid="np-next-2" onClick={() => {
              if (!tipoId) return toast.error("Scegli un tipo pratica");
              if (!valore && selTipo?.importo_default) setValore(String(selTipo.importo_default));
              setStep(3);
            }} disabled={!tipoId}
              className="inline-flex items-center gap-1.5 px-5 py-2 text-sm bg-[#1A2B4A] text-white rounded-md font-semibold hover:bg-[#22345c] transition-all disabled:opacity-40">
              Avanti <ArrowRight className="w-4 h-4" />
            </button>
          )}
          {step === 3 && (
            <div className="flex items-center gap-2">
              <button data-testid="np-save-bozza" onClick={() => submit(true)} disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-[#1A2B4A] text-[#1A2B4A] rounded-md font-semibold hover:bg-[#f9f8f5] transition-all disabled:opacity-40">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Salva bozza
              </button>
              <button data-testid="np-create-pratica" onClick={() => submit(false)} disabled={busy}
                className="inline-flex items-center gap-1.5 px-5 py-2 text-sm bg-[#276749] text-white rounded-md font-semibold hover:opacity-90 transition-all disabled:opacity-40">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Crea pratica
              </button>
            </div>
          )}
        </div>
      </div>

      {uploadOpen && selCli && (
        <UploadDialog cliente={selCli} nomeSuggerito={docAtteso} titolo={docAtteso ? `Carica: ${docAtteso}` : "Carica documenti"}
          onClose={() => { setUploadOpen(false); setDocAtteso(""); }} onUploaded={() => { refetchDrive(); refetchDocStato(); }} />
      )}
    </div>
  );
}
